import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Bulletproof Gregorian date picker.
 * Renders a Popover + react-day-picker calendar so the picker is ALWAYS
 * Gregorian regardless of the device / browser locale (fixes iOS Safari
 * showing Hijri when the OS is set to Arabic).
 *
 * Emits the ISO date string (YYYY-MM-DD) via onChange.
 *
 * Props:
 *   value: 'YYYY-MM-DD' | ''
 *   onChange: (isoString) => void
 *   placeholder, className, testid, disabled
 */
export const GregorianDatePicker = ({
  value,
  onChange,
  placeholder = 'Pick a date',
  className = '',
  testid,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : null;
  const isValid = date && !isNaN(date.getTime());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`w-full justify-start text-left font-normal ${!isValid ? 'text-muted-foreground' : ''} ${className}`}
          data-testid={testid}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {isValid ? format(date, 'dd MMM yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={isValid ? date : undefined}
          onSelect={(d) => {
            if (d) {
              const iso = format(d, 'yyyy-MM-dd');
              onChange(iso);
              setOpen(false);
            }
          }}
          initialFocus
          captionLayout="dropdown"
          fromYear={2000}
          toYear={2035}
        />
      </PopoverContent>
    </Popover>
  );
};

export default GregorianDatePicker;
